import type { InsuranceDrpSettings } from '../../hooks/useGetSettings';

/**
 * A DRP row is a set of rules, not a single carrier. Carriers in the same group
 * that work the same way share one entry: the parent owns the rules and its sub
 * companies point at it through `parentInsurerId`. Branching a sub company
 * clears that pointer, so it becomes its own row — still labelled with the
 * parent it came from, e.g. "Allstate -> SafeAuto".
 *
 * The stored shape stays one document per insurer, which is what the rest of
 * the platform looks carriers up by; only the way they are grouped is new.
 */
export interface InsuranceRuleSet {
    /** The carrier whose configuration the whole set runs on. */
    primary: InsuranceDrpSettings;
    /** Sub companies covered by the primary's rules; they have no row. */
    included: Array<InsuranceDrpSettings>;
}

/** Words that name the group rather than a carrier in it. */
const GROUP_SUFFIX = /[\s,]+(insurance\s+)?(group|groups|companies|company|cos?\.?|inc\.?|corp\.?)$/i;

/** Punctuation and legal suffixes dropped before comparing two carrier names. */
const normalizeName = (name: string): string =>
    name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/** "USAA Insurance Group" reads as "USAA" in front of a sub company. */
export const groupDisplayName = (groupName?: string): string | undefined => {
    if (!groupName) return undefined;
    const trimmed = groupName.replace(GROUP_SUFFIX, '').trim();
    return trimmed.length > 0 ? trimmed : groupName.trim();
};

/**
 * The parent to show in front of a carrier's name, or nothing when the carrier
 * is the parent — or PartBay has no group for it.
 *
 * A carrier added since groups were tracked carries its parent's name outright.
 * For everything saved before that, the group is what is known, so the parent is
 * the configured group mate whose name is the group's — "USAA" in the USAA
 * group, which is what makes Garrison read as "USAA -> Garrison". Nothing is
 * shown when that carrier is not a DRP partner: group names are holding
 * companies as often as carriers, and a prefix naming a company the shop has no
 * relationship with is noise rather than context.
 */
export const groupParentLabel = (
    entry: {
        insurerId?: string;
        insurerName: string;
        groupId?: string;
        groupName?: string;
        parentInsurerName?: string;
    },
    _insurance: Array<InsuranceDrpSettings>,
): string | undefined => {
    if (entry.parentInsurerName) return entry.parentInsurerName;
    const groupName = groupDisplayName(entry.groupName);
    if (!groupName || !entry.groupId) return undefined;
    if (normalizeName(entry.insurerName) === normalizeName(groupName)) return undefined;
    return undefined;
};

/**
 * Estimate matching is the fallback: with CCC, PartsTrader, and custom all off,
 * it is on. It can also stay on next to CCC or PartsTrader. Custom matching is
 * left alone — this only fills the gap when the other three are off.
 */
export const withAutomaticPriceMatchEstimate = <T extends {
    priceMatchEstimate: boolean;
    priceMatchCCC: boolean;
    priceMatchPartstrader: boolean;
    priceMatchCustom: boolean;
}>(flags: T): T => {
    const othersOff = !flags.priceMatchCCC && !flags.priceMatchPartstrader && !flags.priceMatchCustom;
    if (!othersOff || flags.priceMatchEstimate) return flags;
    return { ...flags, priceMatchEstimate: true };
};

/** The rules a sub company inherits from its parent, i.e. everything but identity. */
export const ruleFieldsOf = (entry: InsuranceDrpSettings) => ({
    locations: entry.locations,
    prohibitedSuppliers: entry.prohibitedSuppliers,
    note: entry.note,
    priceMatchEstimate: entry.priceMatchEstimate,
    priceMatchCCC: entry.priceMatchCCC,
    priceMatchPartstrader: entry.priceMatchPartstrader,
    priceMatchCustom: entry.priceMatchCustom,
    priceMatchCustomRanking: entry.priceMatchCustomRanking,
    orderingThreshold: entry.orderingThreshold,
});

/** True when this entry follows another entry's rules instead of its own. */
export const isIncludedSub = (
    _entry: InsuranceDrpSettings,
    _byId: Map<string, InsuranceDrpSettings>,
): boolean => false;

/**
 * Collapse the flat insurance array into one rule set per row, keeping the
 * saved order of the carriers that own rules. A sub company whose parent is no
 * longer configured stands on its own rather than disappearing.
 */
export const buildRuleSets = (
    insurance: Array<InsuranceDrpSettings>,
): Array<InsuranceRuleSet> => {
    const byId = new Map(insurance.map((entry) => [entry.insurerId, entry]));
    const sets = new Map<string, InsuranceRuleSet>();

    for (const entry of insurance) {
        if (isIncludedSub(entry, byId)) continue;
        sets.set(entry.insurerId, { primary: entry, included: [] });
    }

    for (const entry of insurance) {
        if (!isIncludedSub(entry, byId)) continue;
        sets.set(entry.insurerId, { primary: entry, included: [] });
    }

    return Array.from(sets.values()).map((set) => ({
        ...set,
        included: [...set.included].sort((a, b) => a.insurerName.localeCompare(b.insurerName)),
    }));
};

/**
 * A group mate this carrier could be folded into: one that owns its rules, is
 * not a sub of this carrier, and is the group's parent where that can be told.
 */
export const mergeTargetFor = (
    _entry: { insurerId: string; insurerName?: string; groupId?: string; groupName?: string },
    _insurance: Array<InsuranceDrpSettings>,
): { insurerId: string; insurerName: string } | undefined => undefined;

/** Every carrier name a rule set covers, for search. */
export const ruleSetNames = (set: InsuranceRuleSet): Array<string> => [
    set.primary.insurerName,
    ...set.included.map((sub) => sub.insurerName),
];

/**
 * Stage a rule change on a whole set: the primary and every sub company that
 * follows it, so an inline edit keeps them identical by construction.
 */
export const applyRuleChange = (
    insurance: Array<InsuranceDrpSettings>,
    set: InsuranceRuleSet,
    changes: Partial<InsuranceDrpSettings>,
): Array<InsuranceDrpSettings> => {
    const ids = new Set([set.primary.insurerId, ...set.included.map((sub) => sub.insurerId)]);
    return insurance.map((entry) => (ids.has(entry.insurerId) ? { ...entry, ...changes } : entry));
};

/** Drop a rule set: the primary and everything that only existed under it. */
export const removeRuleSet = (
    insurance: Array<InsuranceDrpSettings>,
    set: InsuranceRuleSet,
): Array<InsuranceDrpSettings> => {
    const ids = new Set([set.primary.insurerId, ...set.included.map((sub) => sub.insurerId)]);
    return insurance.filter((entry) => !ids.has(entry.insurerId));
};

/** Drop one sub company, leaving the rest of its set alone. */
export const removeSubCompany = (
    insurance: Array<InsuranceDrpSettings>,
    subInsurerId: string,
): Array<InsuranceDrpSettings> =>
    insurance.filter((entry) => entry.insurerId !== subInsurerId);

/**
 * Give a sub company its own rules. It starts from the parent's configuration —
 * branching is a starting point, not a reset — and keeps the parent's name so
 * the new row reads "Allstate -> SafeAuto".
 */
export const branchSubCompany = (
    insurance: Array<InsuranceDrpSettings>,
    _subInsurerId: string,
): Array<InsuranceDrpSettings> => insurance;

/** Fold a branched sub company back under a parent's rules. */
export const mergeSubCompany = (
    insurance: Array<InsuranceDrpSettings>,
    subInsurerId: string,
    parent: InsuranceDrpSettings,
): Array<InsuranceDrpSettings> =>
    insurance.map((entry) =>
        entry.insurerId === subInsurerId
            ? {
                  ...entry,
                  ...ruleFieldsOf(parent),
              }
            : entry,
    );
